import {
    ToolMode,
    MouseButton,
    ModifierKey,
    type ToolBinding,
    type ToolBindingOptions,
    type ToolConfiguration,
} from './enums';
import { ToolRegistry } from './tool-registry';
import type { ITool } from './types';
import type { IServiceManager } from '../core/types';
import type { IViewport } from '../viewports/types';

/**
 * A ToolGroup manages a set of tools and their mouse button bindings for one or more viewports.
 *
 * Inspired by Cornerstone3D's ToolGroup concept:
 * - Each viewport belongs to one ToolGroup
 * - Multiple tools can be simultaneously Active on different mouse buttons
 * - Binding resolution maps (button + modifier) → tool per event
 */
export class ToolGroup {
    private toolConfigs = new Map<string, ToolConfiguration>();
    private toolInstances = new Map<string, ITool>();
    private viewports = new Set<IViewport>();

    constructor(
        readonly id: string,
        private readonly services: IServiceManager
    ) {}

    // ── Tool Registration ──

    /**
     * Add a tool to this group (starts as Disabled with no bindings).
     * The tool instance is created lazily when first needed.
     */
    addTool(toolId: string): void {
        if (this.toolConfigs.has(toolId)) {
            return;
        }
        if (!ToolRegistry.has(toolId)) {
            console.error(`[ToolGroup:${this.id}] Cannot add unknown tool '${toolId}'`);
            return;
        }
        this.toolConfigs.set(toolId, { mode: ToolMode.DISABLED, bindings: [] });
    }

    removeTool(toolId: string): void {
        const instance = this.toolInstances.get(toolId);
        if (instance) {
            try {
                instance.deactivate();
            } catch {
                // Tool may not have been activated
            }
            this.toolInstances.delete(toolId);
        }
        this.toolConfigs.delete(toolId);
    }

    /**
     * Vérifie si un outil est ajouté à ce ToolGroup
     */
    hasTool(toolId: string): boolean {
        return this.toolConfigs.has(toolId);
    }

    // ── Mode Management ──

    /**
     * Set a tool to Active with specified bindings.
     *
     * Multiple tools can be Active on the same button. When an event occurs,
     * all matching tools are tried in order until one consumes the event.
     * This enables click/drag distinction: e.g., LocationPlacer handles mouse_click
     * while Pan handles mouse_drag, both bound to the Primary button.
     *
     * Use setToolPassive() to explicitly demote a tool when switching modes
     * (e.g., in CameraInteractionModeSelector).
     */
    setToolActive(toolId: string, options: ToolBindingOptions): void {
        const config = this.toolConfigs.get(toolId);
        if (!config) {
            console.warn(`[ToolGroup:${this.id}] Tool '${toolId}' not added to group`);
            return;
        }

        config.mode = ToolMode.ACTIVE;
        config.bindings = [...options.bindings];

        // Ensure tool instance exists
        this.getOrCreateInstance(toolId);
    }

    setToolPassive(toolId: string): void {
        const config = this.toolConfigs.get(toolId);
        if (!config) return;
        config.mode = ToolMode.PASSIVE;
        config.bindings = [];
    }

    setToolEnabled(toolId: string): void {
        const config = this.toolConfigs.get(toolId);
        if (!config) return;
        config.mode = ToolMode.ENABLED;
        config.bindings = [];
    }

    setToolDisabled(toolId: string): void {
        const config = this.toolConfigs.get(toolId);
        if (!config) return;
        config.mode = ToolMode.DISABLED;
        config.bindings = [];
    }

    // ── Binding Manipulation ──

    /**
     * Add a single binding to a tool, setting it Active if not already.
     * Deduplicates: won't add if an identical (mouseButton + modifierKey) binding exists.
     */
    addToolBinding(toolId: string, binding: ToolBinding): void {
        const config = this.toolConfigs.get(toolId);
        if (!config) {
            console.warn(`[ToolGroup:${this.id}] Tool '${toolId}' not added to group`);
            return;
        }

        const normalizedModifier = binding.modifierKey ?? ModifierKey.NONE;
        const alreadyBound = config.bindings.some(
            (b) =>
                b.mouseButton === binding.mouseButton &&
                (b.modifierKey ?? ModifierKey.NONE) === normalizedModifier
        );

        if (!alreadyBound) {
            config.bindings.push({ ...binding });
        }

        config.mode = ToolMode.ACTIVE;
        this.getOrCreateInstance(toolId);
    }

    /**
     * Remove a specific binding from a tool.
     * If the tool has no remaining bindings, it transitions to Passive mode.
     */
    removeToolBinding(toolId: string, binding: ToolBinding): void {
        const config = this.toolConfigs.get(toolId);
        if (!config) return;

        const normalizedModifier = binding.modifierKey ?? ModifierKey.NONE;
        config.bindings = config.bindings.filter(
            (b) =>
                !(
                    b.mouseButton === binding.mouseButton &&
                    (b.modifierKey ?? ModifierKey.NONE) === normalizedModifier
                )
        );

        if (config.bindings.length === 0) {
            config.mode = ToolMode.DISABLED;
        }
    }

    // ── Binding Resolution ──

    /**
     * Find all Active tools bound to the given mouse button + modifier combination.
     * Returns tools in registration order. The caller should try each until one consumes.
     */
    getToolsForBinding(button: MouseButton, modifier: ModifierKey = ModifierKey.NONE): ITool[] {
        const tools: ITool[] = [];
        for (const [toolId, config] of this.toolConfigs) {
            if (config.mode !== ToolMode.ACTIVE) continue;

            const match = config.bindings.some(
                (b) => b.mouseButton === button && (b.modifierKey ?? ModifierKey.NONE) === modifier
            );

            if (match) {
                const instance = this.getOrCreateInstance(toolId);
                if (instance) tools.push(instance);
            }
        }
        return tools;
    }

    /**
     * Find all Active tools bound to the Wheel pseudo-button.
     */
    getToolsForWheelBinding(): ITool[] {
        return this.getToolsForBinding(MouseButton.WHEEL);
    }

    // ── Viewport Management ──

    addViewport(viewport: IViewport): void {
        this.viewports.add(viewport);
    }

    removeViewport(viewportId: string): void {
        for (const vp of this.viewports) {
            if (vp.id === viewportId) {
                this.viewports.delete(vp);
                break;
            }
        }
    }

    getViewports(): ReadonlySet<IViewport> {
        return this.viewports;
    }

    // ── Querying ──

    getToolInstance(toolId: string): ITool | null {
        return this.toolInstances.get(toolId) ?? null;
    }

    getToolMode(toolId: string): ToolMode | null {
        return this.toolConfigs.get(toolId)?.mode ?? null;
    }

    getToolsWithMode(mode: ToolMode): ITool[] {
        const tools: ITool[] = [];
        for (const [toolId, config] of this.toolConfigs) {
            if (config.mode === mode) {
                const instance = this.getOrCreateInstance(toolId);
                if (instance) tools.push(instance);
            }
        }
        return tools;
    }

    // ── Lifecycle ──

    dispose(): void {
        for (const instance of this.toolInstances.values()) {
            try {
                instance.deactivate();
            } catch {
                // Ignore cleanup errors
            }
        }
        this.toolInstances.clear();
        this.toolConfigs.clear();
        this.viewports.clear();
    }

    // ── Private ──

    private getOrCreateInstance(toolId: string): ITool | null {
        const existing = this.toolInstances.get(toolId);
        if (existing) return existing;

        const instance = ToolRegistry.create(toolId, this.services);
        if (instance) {
            this.toolInstances.set(toolId, instance);
        }
        return instance;
    }
}
